/**
 * Refresh bid handler.
 */
import { createLogger } from '../../../shared/logger.js';
import { refreshLotBidsRemote } from '../../modules/api-operations';
import { mapRemoteDocument } from '../../modules/lotOperations';
import { showToast } from '../../modules/ui';
import type { TableHandlerDeps } from './types';

const log = createLogger('portal:tableHandlers:refresh');

/**
 * Update bid refresh button state in-place without full render.
 * Sets or clears the disabled state on the button.
 */
function updateBidButtonState(lotId: string, isPending: boolean): void {
  const button = document.querySelector(`button[data-action="refresh-bid"][data-id="${lotId}"]`);
  if (button instanceof HTMLButtonElement) {
    button.disabled = isPending;
  }
}

export async function handleRefreshBid(
  id: string,
  e: Event,
  deps: TableHandlerDeps
): Promise<void> {
  const { state, render, ensureAccessToken, DEBUG } = deps;

  const lot = state.lots.find((x: unknown) => (x as { id: string }).id === id);
  if (!lot) return;

  e.preventDefault();

  if (!state.pendingBidRefreshIds) {
    state.pendingBidRefreshIds = new Set();
  }
  state.pendingBidRefreshIds.add(lot.id);

  // Update button state in-place instead of full render
  updateBidButtonState(lot.id, true);

  const ensureToken = async (retry?: boolean): Promise<string> => {
    const token = await ensureAccessToken(retry || false);
    if (!token) {
      throw new Error('Unable to acquire access token.');
    }
    return token;
  };

  try {
    await refreshLotBidsRemote(lot.id, false, state, ensureToken, mapRemoteDocument, render, DEBUG);
    if (state.pendingBidRefreshIds) {
      state.pendingBidRefreshIds.delete(lot.id);
      // Update button state in-place instead of full render
      updateBidButtonState(lot.id, false);
    }
  } catch (err: unknown) {
    log.error('Failed to refresh bid:', err);
    showToast('Failed to refresh bid. Please try again.', { type: 'error' });
    if (state.pendingBidRefreshIds) {
      state.pendingBidRefreshIds.delete(lot.id);
      // Update button state in-place instead of full render
      updateBidButtonState(lot.id, false);
    }
  }
}
